import { Controller } from "@hotwired/stimulus"

// Client-side filter + paginator for the dealer dashboard submissions list.
// Computes which rows match the current filter set, slices the match list into
// pages, and shows only the current page's slice. State (filters + page) is
// mirrored to the URL query string.
export default class extends Controller {
  static targets = [
    "search",
    "status",
    "type",
    "verdict",
    "dealership",
    "drafts",
    "panel",
    "panelChevron",
    "body",
    "row",
    "visibleCount",
    "totalCount",
    "costTotal",
    "empty",
    "pagination",
    "prevPage",
    "nextPage",
    "pageNumbers",
    "position"
  ]
  static values = { pageSize: { type: Number, default: 6 } }

  declare searchTarget: HTMLInputElement
  declare statusTarget: HTMLSelectElement
  declare typeTarget: HTMLSelectElement
  declare verdictTarget: HTMLSelectElement
  declare hasDealershipTarget: boolean
  declare dealershipTarget: HTMLSelectElement
  declare draftsTarget: HTMLInputElement
  declare panelTarget: HTMLElement
  declare panelChevronTarget: HTMLElement
  declare bodyTarget: HTMLElement
  declare rowTargets: HTMLElement[]
  declare visibleCountTarget: HTMLElement
  declare totalCountTarget: HTMLElement
  declare hasCostTotalTarget: boolean
  declare costTotalTarget: HTMLElement
  declare emptyTarget: HTMLElement
  declare paginationTarget: HTMLElement
  declare prevPageTarget: HTMLButtonElement
  declare nextPageTarget: HTMLButtonElement
  declare pageNumbersTarget: HTMLElement
  declare positionTarget: HTMLElement
  declare pageSizeValue: number

  private page = 1

  connect(): void {
    this.restoreFromUrl()
    this.render()
  }

  // Triggered by filter input changes — always resets to page 1.
  filter(): void {
    this.page = 1
    this.render()
  }

  reset(): void {
    this.searchTarget.value = ""
    this.statusTarget.value = ""
    this.typeTarget.value = ""
    this.verdictTarget.value = ""
    if (this.hasDealershipTarget) this.dealershipTarget.value = ""
    this.draftsTarget.checked = false
    this.page = 1
    this.render()
  }

  togglePanel(): void {
    const open = !this.panelTarget.classList.toggle("hidden")
    this.panelChevronTarget.classList.toggle("rotate-90", open)
  }

  // Click-through filters from stats tiles etc. The triggering element sets
  // `data-filter-key` (status/type/verdict/dealership) + `data-filter-value`.
  // Opens the filter panel on apply so the user sees what's active.
  applyFilter(event: Event): void {
    const el = event.currentTarget as HTMLElement
    const key = el.dataset.filterKey ?? ""
    const value = el.dataset.filterValue ?? ""
    const input = this.filterInputFor(key)
    if (!input) return

    // Toggle off when re-clicking the already-active filter.
    input.value = input.value === value ? "" : value

    // Reveal the panel so the user can see/adjust.
    this.panelTarget.classList.remove("hidden")
    this.panelChevronTarget.classList.add("rotate-90")

    this.filter()
  }

  private filterInputFor(key: string): HTMLSelectElement | null {
    switch (key) {
      case "status": return this.statusTarget
      case "type": return this.typeTarget
      case "verdict": return this.verdictTarget
      case "dealership": return this.hasDealershipTarget ? this.dealershipTarget : null
      default: return null
    }
  }

  prevPage(): void {
    if (this.page > 1) {
      this.page--
      this.render()
    }
  }

  nextPage(): void {
    this.page++
    this.render()
  }

  goToPage(event: Event): void {
    const target = event.currentTarget as HTMLElement
    const p = Number(target.dataset.page)
    if (p && p !== this.page) {
      this.page = p
      this.render()
    }
  }

  private render(): void {
    const filters = this.readFilters()
    const matching = this.rowTargets.filter((row) => this.rowMatches(row, filters))

    const totalPages = Math.max(1, Math.ceil(matching.length / this.pageSizeValue))
    if (this.page > totalPages) this.page = totalPages
    if (this.page < 1) this.page = 1

    const start = (this.page - 1) * this.pageSizeValue
    const end = start + this.pageSizeValue
    const visibleSlice = matching.slice(start, end)
    const visibleSet = new Set(visibleSlice)

    let costCents = 0
    this.rowTargets.forEach((row) => {
      const show = visibleSet.has(row)
      row.hidden = !show
      if (show) costCents += Number(row.dataset.costCents ?? 0)
    })

    this.visibleCountTarget.textContent = String(matching.length)
    this.emptyTarget.classList.toggle(
      "hidden",
      matching.length !== 0 || this.rowTargets.length === 0
    )

    if (this.hasCostTotalTarget) {
      this.costTotalTarget.textContent = `$${(costCents / 100).toFixed(4)}`
    }

    this.renderPagination(matching.length, totalPages)
    this.updateUrl(filters)
  }

  private readFilters() {
    return {
      search: this.searchTarget.value.trim().toLowerCase(),
      status: this.statusTarget.value,
      type: this.typeTarget.value,
      verdict: this.verdictTarget.value,
      dealership: this.hasDealershipTarget ? this.dealershipTarget.value : "",
      showDrafts: this.draftsTarget.checked
    }
  }

  private rowMatches(row: HTMLElement, f: ReturnType<typeof this.readFilters>): boolean {
    const isDraft = row.dataset.status === "draft"
    const matchesSearch = !f.search || (row.dataset.search ?? "").includes(f.search)
    const matchesStatus = !f.status || row.dataset.status === f.status
    const matchesType = !f.type || row.dataset.type === f.type
    const matchesVerdict = !f.verdict || row.dataset.verdict === f.verdict
    const matchesDealership = !f.dealership || row.dataset.dealership === f.dealership
    // Drafts hidden by default unless the checkbox is on, or the user
    // explicitly asked for them via the status dropdown.
    const matchesDraftPref = !isDraft || f.showDrafts || f.status === "draft"

    return matchesSearch && matchesStatus && matchesType && matchesVerdict &&
           matchesDealership && matchesDraftPref
  }

  private renderPagination(totalMatching: number, totalPages: number): void {
    // Hide paginator entirely when one page or fewer.
    this.paginationTarget.classList.toggle("hidden", totalPages <= 1)

    this.prevPageTarget.disabled = this.page <= 1
    this.nextPageTarget.disabled = this.page >= totalPages

    this.pageNumbersTarget.innerHTML = ""
    for (const p of this.pageRange(totalPages)) {
      if (p === "…") {
        const span = document.createElement("span")
        span.className = "px-1 opacity-40 text-xs"
        span.textContent = "…"
        this.pageNumbersTarget.appendChild(span)
        continue
      }
      const btn = document.createElement("button")
      btn.type = "button"
      btn.textContent = String(p)
      btn.className = p === this.page
        ? "btn btn-xs btn-accent w-8"
        : "btn btn-xs btn-ghost w-8"
      btn.dataset.page = String(p)
      btn.dataset.action = "click->submissions-filter#goToPage"
      this.pageNumbersTarget.appendChild(btn)
    }

    if (totalMatching === 0) {
      this.positionTarget.textContent = ""
    } else {
      const from = (this.page - 1) * this.pageSizeValue + 1
      const to = Math.min(from + this.pageSizeValue - 1, totalMatching)
      this.positionTarget.textContent = `${from}–${to} of ${totalMatching}`
    }
  }

  private pageRange(totalPages: number): (number | "…")[] {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const range: (number | "…")[] = [ 1 ]
    const start = Math.max(2, this.page - 1)
    const end = Math.min(totalPages - 1, this.page + 1)
    if (start > 2) range.push("…")
    for (let p = start; p <= end; p++) range.push(p)
    if (end < totalPages - 1) range.push("…")
    range.push(totalPages)
    return range
  }

  private restoreFromUrl(): void {
    const params = new URLSearchParams(window.location.search)
    this.searchTarget.value = params.get("q") ?? ""
    this.statusTarget.value = params.get("status") ?? ""
    this.typeTarget.value = params.get("type") ?? ""
    this.verdictTarget.value = params.get("verdict") ?? ""
    if (this.hasDealershipTarget) this.dealershipTarget.value = params.get("dealership") ?? ""
    this.draftsTarget.checked = params.get("drafts") === "1"
    this.page = Math.max(1, Number(params.get("page") ?? 1) || 1)

    const hasActiveFilter = Boolean(
      this.searchTarget.value ||
        this.statusTarget.value ||
        this.typeTarget.value ||
        this.verdictTarget.value ||
        (this.hasDealershipTarget && this.dealershipTarget.value)
    )
    if (hasActiveFilter) {
      this.panelTarget.classList.remove("hidden")
      this.panelChevronTarget.classList.add("rotate-90")
    }
  }

  private updateUrl(f: ReturnType<typeof this.readFilters>): void {
    const params = new URLSearchParams()
    if (f.search) params.set("q", f.search)
    if (f.status) params.set("status", f.status)
    if (f.type) params.set("type", f.type)
    if (f.verdict) params.set("verdict", f.verdict)
    if (f.dealership) params.set("dealership", f.dealership)
    if (f.showDrafts) params.set("drafts", "1")
    if (this.page > 1) params.set("page", String(this.page))
    const qs = params.toString()
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    window.history.replaceState(null, "", url)
  }
}
